<template>
  <div class="audio-page">
    <div class="bg-decoration">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
    </div>

    <div class="content-wrapper">
      <aside class="sidebar">
        <nav class="nav-card">
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <div class="brand-copy">
              <h1 class="brand-name">树苗英语</h1>
              <span class="brand-subtitle">TREE SAPLING</span>
            </div>
          </div>

          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
          <div class="nav-menu-visual" aria-hidden="true">
            <img :src="menuBgImage" alt="" class="nav-menu-image" />
          </div>
        </nav>
      </aside>

      <main class="audio-shell" :class="{ 'has-player': currentAudioTitle }">
        <div class="mobile-topbar" :class="{ 'is-scrolled': isTopbarScrolled }">
          <button type="button" class="mobile-back-btn" @click="handleGoBack" aria-label="返回上一页">
            <font-awesome-icon icon="chevron-left" />
          </button>
          <h1 class="mobile-topbar-title">{{ mobilePageTitle }}</h1>
          <span class="mobile-topbar-spacer" aria-hidden="true"></span>
        </div>
        <div class="mobile-topbar-offset" aria-hidden="true"></div>

        <header class="audio-header">
          <button type="button" class="back-btn" @click="handleGoBack" aria-label="返回上一页">
            <font-awesome-icon icon="chevron-left" />
          </button>
        </header>

        <section class="series-hero" aria-label="音频系列信息">
          <div class="series-cover-wrap">
            <el-skeleton v-if="shouldShowSeriesCoverSkeleton" animated class="series-cover-skeleton">
              <template #template>
                <el-skeleton-item variant="image" class="series-cover-skeleton-item" />
              </template>
            </el-skeleton>
            <img
              v-if="seriesCoverUrl && !isSeriesCoverLoadFailed"
              v-show="!isSeriesCoverLoading"
              :src="seriesCoverUrl"
              :alt="seriesTitle"
              class="series-cover"
              @load="handleSeriesCoverLoad"
              @error="handleSeriesCoverError"
            />
          </div>
          <div class="series-copy">
            <h1 class="series-title">{{ seriesTitle }}</h1>
            <p class="series-subtitle">{{ seriesSubtitle }}</p>
            <div class="series-age-row">
              <span
                v-for="ageRange in routeStageAgeRanges"
                :key="ageRange"
                class="series-age-badge"
              >
                {{ ageRange }}
              </span>
            </div>
            <p v-if="seriesDescription" class="series-description">{{ seriesDescription }}</p>
          </div>
        </section>

        <h2 class="track-section-title">音频列表（{{ audioListCount }}）</h2>

        <section ref="audioListRef" class="audio-list track-list-panel" aria-label="音频列表">
        <template v-if="showAudioSkeleton">
          <div
            v-for="index in queryParams.pageSize"
            :key="`audio-skeleton-${index}`"
            class="audio-item audio-skeleton-item"
          >
            <el-skeleton animated>
              <template #template>
                <div class="audio-skeleton-row">
                  <el-skeleton-item variant="circle" class="audio-skeleton-icon" />
                  <div class="audio-skeleton-copy">
                    <el-skeleton-item variant="text" class="audio-skeleton-title" />
                    <el-skeleton-item variant="text" class="audio-skeleton-date" />
                  </div>
                  <el-skeleton-item variant="button" class="audio-skeleton-button" />
                </div>
              </template>
            </el-skeleton>
          </div>
        </template>

        <article
          v-show="!showAudioSkeleton"
          v-for="item in visibleAudioItems"
          :key="item.id"
          class="audio-item"
          :class="{ playing: currentAudioItemId === item.id }"
          @click="playAudio(item)"
        >
          <button type="button" class="track-play-btn" :aria-label="`播放 ${item.title}`">
            <font-awesome-icon :icon="currentAudioItemId === item.id ? 'pause' : 'play'" />
          </button>
          <div class="audio-item-copy">
            <h3 class="audio-title">
              <span class="track-index">{{ item.indexText }}</span>
              <span>{{ item.title }}</span>
            </h3>
          </div>
          <button
            type="button"
            class="favorite-btn"
            :class="{ active: item.isFavorite }"
            :disabled="item.isFavoriteLoading"
            :aria-label="item.isFavorite ? '取消收藏' : '收藏'"
            @click.stop="toggleFavorite(item.id)"
          >
            <font-awesome-icon :icon="item.isFavorite ? 'heart-circle-check' : 'heart'" />
            <span>{{ item.isFavorite ? '已收藏' : '收藏' }}</span>
          </button>
        </article>

        <div v-if="isAudioPageLoading" class="audio-page-loading">
          <el-skeleton animated>
            <template #template>
              <el-skeleton-item variant="text" class="audio-page-loading-line" />
            </template>
          </el-skeleton>
        </div>

        <div v-if="!showAudioSkeleton && visibleAudioItems.length === 0" class="empty-state">
          <font-awesome-icon icon="headphones" />
          <p>没有音频内容</p>
        </div>
      </section>

      <Transition name="player-dock">
        <section v-if="currentAudioTitle" class="audio-player-dock" aria-label="底部音频播放器">
          <div class="audio-player-head">
            <div class="audio-player-now">
              <span class="audio-player-label">正在播放</span>
              <strong class="audio-player-title">{{ currentAudioTitle }}</strong>
            </div>
          </div>
          <audio
            ref="audioPlayerRef"
            class="audio-player"
            :src="currentAudioSrc"
            controls
            preload="metadata"
            @play="handleAudioPlay"
            @timeupdate="handleAudioTimeUpdate"
            @seeking="handleAudioSeeking"
            @seeked="handleAudioSeeked"
            @ended="handleAudioEnded"
          ></audio>
        </section>
      </Transition>
      </main>
    </div>

    <ParentPasswordModal ref="parentPasswordModalRef" />
    <LoginPromptModal v-model="isLoginPromptOpen" @confirm="goLogin" />
    <VipPromptModal
      v-model="isAudioVipPromptOpen"
      description="成为会员后可收听完整内容"
      @confirm="goVip"
    />
  </div>
</template>

<script setup lang="ts">
import logo2Image from '../assets/logo2.png'
import menuBgImage from '../assets/menu_bg.jpg'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { getAudioPage, getAudioSeries, type AudioSeries, type AudioVO } from '@/api/course-audio'
import { addFavorites, deleteFavorites, getFavoritesPage, type FavoritesVO } from '@/api/favorites'
import { addHistory, getHistoryPage, type HistoryVO } from '@/api/history'
import { getAccessToken } from '@/config/axios/auth'
import LoginPromptModal from '@/components/LoginPromptModal.vue'
import VipPromptModal from '@/components/VipPromptModal.vue'

const isGuestUser = computed(() => getAccessToken() == null)
const route = useRoute()
const router = useRouter()
const AUDIO_CONTENT_TYPE = 2
const AUDIO_PREVIEW_LIMIT_SECONDS = 30
const isLoginPromptOpen = ref(false)
const isAudioVipPromptOpen = ref(false)
const isNonVipUser = computed(() =>
  !isGuestUser.value && localStorage.getItem('vipTag') === 'false'
)
const shouldApplyAudioPreviewLimit = computed(() => isGuestUser.value || isNonVipUser.value)
const audioPreviewWatchedSeconds = ref(0)
const isAudioPreviewLimitReached = ref(false)
let isAudioSeeking = false
let lastTrackedAudioSecond = 0
let audioSeekStartSecond = 0

const tabs = [
  { id: 'all', label: '全部' },
  { id: 'favorites', label: '收藏' },
  { id: 'recent', label: '最近' }
]

const queryParams = ref({
  pageNo: 1,
  pageSize: 10,
  stage: route.query.stage,
  seriesId: route.query.seriesId
})

const audioList = ref<AudioVO[]>([])
const audioSeries = ref<AudioSeries | null>(null)
const isSeriesCoverLoading = ref(true)
const isSeriesCoverLoadFailed = ref(false)
const favoriteAudioList = ref<FavoritesVO[]>([])
const historyAudioList = ref<HistoryVO[]>([])
const dataTotal = ref(0)
const isLoading = ref(false)

const activeTab = ref('all')
const activeNav = ref('')
const isTopbarScrolled = ref(false)
const currentAudioItemId = ref('')
const currentAudioTitle = ref('')
const audioPlayerRef = ref(null)
const AUTH_STORAGE_KEY = 'tree-sprout-auth'
const PARENT_VERIFIED_STORAGE_KEY = 'tree-sprout-parent-verified'
const parentPasswordModalRef = ref(null)
const audioListRef = ref<HTMLElement | null>(null)
let audioScrollContainer: HTMLElement | null = null

const openLoginPrompt = () => {
  isLoginPromptOpen.value = true
}

const goLogin = () => {
  closeLoginPrompt()
  router.push('/login')
}

const closeLoginPrompt = () => {
  isLoginPromptOpen.value = false
}

const goVip = () => {
  isAudioVipPromptOpen.value = false
  router.push('/vip')
}

const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看历史', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]

const mobilePageTitle = computed(() => {
  const title = typeof route.query.title === 'string' ? route.query.title.trim() : ''
  return title || '音频库'
})

const currentAudioSrc = ref('')
const favoriteStateOverrides = ref<Record<number, boolean>>({})
const favoriteLoadingIds = ref<number[]>([])

const stageAgeRangeMap: Record<string, string> = {
  sense: '0-3岁',
  sprout: '3-6岁',
  expression: '6-9岁',
  ability: '9-12岁',
  1: '0-3岁',
  2: '3-6岁',
  3: '6-9岁',
  4: '9-12岁'
}

const routeStage = computed(() => {
  const stage = Array.isArray(route.query.stage) ? route.query.stage[0] : route.query.stage
  return String(stage || '1')
})

const normalizeAgeRange = (value: string) => {
  const normalizedValue = value.trim()

  if (!normalizedValue) {
    return ''
  }

  return stageAgeRangeMap[normalizedValue] ||
    (normalizedValue.endsWith('岁') ? normalizedValue : `${normalizedValue}岁`)
}

const routeStageAgeRanges = computed(() => {
  const ageRanges = routeStage.value
    .split(',')
    .map(normalizeAgeRange)
    .filter(Boolean)

  return ageRanges.length ? ageRanges : ['0-3岁']
})

const seriesTitle = computed(() => {
  const queryTitle = typeof route.query.title === 'string' ? route.query.title.trim() : ''
  return audioSeries.value?.chineseName || queryTitle || '音频课程'
})

const seriesSubtitle = computed(() => {
  return audioSeries.value?.englishName || 'Tree Sapling Audio'
})

const seriesDescription = computed(() => audioSeries.value?.description || '')
const seriesCoverUrl = computed(() => audioSeries.value?.coverUrl || '')
const shouldShowSeriesCoverSkeleton = computed(() => !seriesCoverUrl.value || isSeriesCoverLoading.value || isSeriesCoverLoadFailed.value)
const audioListCount = computed(() => dataTotal.value || currentAudioListLength.value)

watch(seriesCoverUrl, () => {
  isSeriesCoverLoading.value = true
  isSeriesCoverLoadFailed.value = false
})

const handleSeriesCoverLoad = () => {
  isSeriesCoverLoading.value = false
  isSeriesCoverLoadFailed.value = false
}

const handleSeriesCoverError = () => {
  isSeriesCoverLoading.value = false
  isSeriesCoverLoadFailed.value = true
}

const getApiFavoriteState = (item) => {
  return item.isFavorite === 0
}

const getAudioFavoriteState = (item) => {
  const audioId = item.id || item.contentId

  if (audioId in favoriteStateOverrides.value) {
    return favoriteStateOverrides.value[audioId]
  }

  return getApiFavoriteState(item)
}

const setAudioFavoriteState = (audioId: number, isFavorite: boolean) => {
  favoriteStateOverrides.value = {
    ...favoriteStateOverrides.value,
    [audioId]: isFavorite
  }
}

const audioItems = computed(() => {
  return audioList.value.map((item, index) => ({
    id: item.id,
    indexText: String(index + 1).padStart(2, '0'),
    title: item.name,
    date: item.createTime,
    audioUrl: item.audioUrl,
    isFavorite: getAudioFavoriteState(item),
    isFavoriteLoading: favoriteLoadingIds.value.includes(item.id),
    isRecent: true
  }))
})

const favoriteAudioItems = computed(() => {
  return favoriteAudioList.value.map((item, index) => ({
    id: item.contentId,
    indexText: String(index + 1).padStart(2, '0'),
    favoriteId: item.id,
    title: item.name,
    date: item.createTime,
    audioUrl: item.audioUrl,
    isFavorite: getAudioFavoriteState(item),
    isFavoriteLoading: favoriteLoadingIds.value.includes(item.contentId),
    isRecent: true
  }))
})

const historyAudioItems = computed(() => {
  return historyAudioList.value.map((item, index) => ({
    id: item.contentId,
    indexText: String(index + 1).padStart(2, '0'),
    historyId: item.id,
    title: item.name,
    date: item.createTime,
    audioUrl: item.audioUrl,
    isFavorite: getAudioFavoriteState(item),
    isFavoriteLoading: favoriteLoadingIds.value.includes(item.contentId),
    isRecent: true
  }))
})

const dataTotalPages = computed(() => Math.ceil(dataTotal.value / queryParams.value.pageSize))
const hasNextAudioPage = computed(() => queryParams.value.pageNo < dataTotalPages.value)
const currentAudioListLength = computed(() => {
  if (activeTab.value === 'favorites') {
    return favoriteAudioList.value.length
  }

  if (activeTab.value === 'recent') {
    return historyAudioList.value.length
  }

  return audioList.value.length
})
const showAudioSkeleton = computed(() => isLoading.value && currentAudioListLength.value === 0)
const isAudioPageLoading = computed(() => isLoading.value && currentAudioListLength.value > 0)

const visibleAudioItems = computed(() => {
  if (activeTab.value === 'favorites') {
    return favoriteAudioItems.value
  }

  if (activeTab.value === 'recent') {
    return historyAudioItems.value
  }

  return audioItems.value
})

const selectTab = (tabId) => {
  if (activeTab.value === tabId) {
    return
  }

  activeTab.value = tabId
  queryParams.value.pageNo = 1
  dataTotal.value = 0
  loadAudioList()
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/category')
}

const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const handleNavItemClick = (item) => {
  if (item.id === 'home') {
    router.push('/')
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
  }
}

const toggleFavorite = async (itemId) => {
  if(isGuestUser.value) {
    openLoginPrompt()
    return
  }
  if (favoriteLoadingIds.value.includes(itemId)) {
    return
  }

  const currentItem = visibleAudioItems.value.find((item) => item.id === itemId)
  const isFavorite = currentItem?.isFavorite === true
  const removedFavoriteItems = activeTab.value === 'favorites'
    ? favoriteAudioList.value.filter((item) => item.contentId === itemId)
    : []
  favoriteLoadingIds.value = [...favoriteLoadingIds.value, itemId]
  setAudioFavoriteState(itemId, !isFavorite)

  if (isFavorite && activeTab.value === 'favorites') {
    favoriteAudioList.value = favoriteAudioList.value.filter((item) => item.contentId !== itemId)
    dataTotal.value = Math.max(0, dataTotal.value - removedFavoriteItems.length)
  }

  try {
    if (isFavorite) {
      await deleteFavorites(AUDIO_CONTENT_TYPE, itemId)
    } else {
      await addFavorites({
        contentType: AUDIO_CONTENT_TYPE,
        contentId: itemId
      })
    }
  } catch {
    setAudioFavoriteState(itemId, isFavorite)
    if (removedFavoriteItems.length) {
      favoriteAudioList.value = [...removedFavoriteItems, ...favoriteAudioList.value]
      dataTotal.value += removedFavoriteItems.length
    }
  } finally {
    favoriteLoadingIds.value = favoriteLoadingIds.value.filter((id) => id !== itemId)
  }
}

const resetAudioPreviewLimit = () => {
  audioPreviewWatchedSeconds.value = 0
  isAudioPreviewLimitReached.value = false
  isAudioVipPromptOpen.value = false
  isAudioSeeking = false
  lastTrackedAudioSecond = 0
  audioSeekStartSecond = 0
}

const enforceAudioPreviewLimit = () => {
  if (!shouldApplyAudioPreviewLimit.value || !isAudioPreviewLimitReached.value) {
    return false
  }

  audioPlayerRef.value?.pause()

  if (isGuestUser.value) {
    openLoginPrompt()
  } else if (isNonVipUser.value) {
    isAudioVipPromptOpen.value = true
  }

  return true
}

const addAudioPreviewDelta = (watchedDelta, currentAudioSecond) => {
  if (watchedDelta > 0 && shouldApplyAudioPreviewLimit.value) {
    audioPreviewWatchedSeconds.value = Math.min(
      AUDIO_PREVIEW_LIMIT_SECONDS,
      audioPreviewWatchedSeconds.value + watchedDelta
    )

    if (audioPreviewWatchedSeconds.value >= AUDIO_PREVIEW_LIMIT_SECONDS) {
      isAudioPreviewLimitReached.value = true
    }
  }

  lastTrackedAudioSecond = currentAudioSecond
}

const trackAudioPreviewTime = (audio) => {
  const currentAudioSecond = audio.currentTime
  const previousSecond = lastTrackedAudioSecond ?? currentAudioSecond
  const watchedDelta = Math.max(0, currentAudioSecond - previousSecond)

  addAudioPreviewDelta(watchedDelta, currentAudioSecond)
}

const trackAudioSeekPreviewTime = (audio) => {
  const currentAudioSecond = audio.currentTime
  const watchedDelta = Math.max(0, currentAudioSecond - audioSeekStartSecond)

  addAudioPreviewDelta(watchedDelta, currentAudioSecond)
}

const handleAudioPlay = () => {
  if (enforceAudioPreviewLimit()) {
    return
  }

  lastTrackedAudioSecond = audioPlayerRef.value?.currentTime ?? 0
}

const handleAudioTimeUpdate = () => {
  const audio = audioPlayerRef.value
  if (!audio) {
    return
  }

  if (isAudioSeeking || audio.seeking) {
    return
  }

  if (enforceAudioPreviewLimit()) {
    return
  }

  trackAudioPreviewTime(audio)
  enforceAudioPreviewLimit()
}

const handleAudioSeeking = () => {
  const audio = audioPlayerRef.value
  if (!audio) {
    return
  }

  if (!isAudioSeeking) {
    audioSeekStartSecond = lastTrackedAudioSecond ?? audio.currentTime
  }

  isAudioSeeking = true
}

const handleAudioSeeked = () => {
  const audio = audioPlayerRef.value
  if (!audio) {
    return
  }

  trackAudioSeekPreviewTime(audio)
  isAudioSeeking = false
  enforceAudioPreviewLimit()
}

const playAudio = (item) => {
  currentAudioItemId.value = item.id
  currentAudioTitle.value = item.title
  currentAudioSrc.value = item.audioUrl || ''
  resetAudioPreviewLimit()
  if (!isGuestUser.value) {
    addHistory({ contentType: AUDIO_CONTENT_TYPE, contentId: item.id })
  }

  requestAnimationFrame(() => {
    if (!audioPlayerRef.value || !currentAudioSrc.value) {
      return
    }

    audioPlayerRef.value.currentTime = 0
    audioPlayerRef.value.play().catch(() => {})
  })
}

const handleAudioEnded = () => {
  const queue = visibleAudioItems.value
  const currentIndex = queue.findIndex((item) => item.id === currentAudioItemId.value)

  if (currentIndex === -1) {
    return
  }

  const nextItem = queue[currentIndex + 1]

  if (!nextItem) {
    return
  }

  playAudio(nextItem)
}

const handlePageScroll = () => {
  isTopbarScrolled.value = window.scrollY > 2
  if (isPageNearBottom()) {
    loadNextAudioPage()
  }
}

const isPageNearBottom = () => {
  const scrollContainer = audioListRef.value

  if (scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight + 1) {
    return scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 120
  }

  const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  const pageHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)

  return scrollTop + viewportHeight >= pageHeight - 120
}

const loadNextAudioPage = async () => {
  if (isLoading.value || !hasNextAudioPage.value) {
    return
  }

  queryParams.value.pageNo += 1
  await loadAudioList(true)
}

const loadAudioList = async (append = false) => {
  if (activeTab.value === 'favorites') {
    await getFavoriteAudioList(append)
    return
  }

  if (activeTab.value === 'recent') {
    await getHistoryAudioList(append)
    return
  }

  await getAudioList(append)
}

const loadAudioSeries = async () => {
  const seriesId = Array.isArray(route.query.seriesId) ? route.query.seriesId[0] : route.query.seriesId

  if (!seriesId) {
    return
  }

  try {
    audioSeries.value = await getAudioSeries(seriesId)
  } catch {
    audioSeries.value = null
  }
}

const getAudioList = async (append = false) => {
  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    const data = await getAudioPage(queryParams.value)
    const list = data?.list || []
    audioList.value = append ? [...audioList.value, ...list] : list
    dataTotal.value = data?.total || 0
  } catch {
    if (append) {
      queryParams.value.pageNo = Math.max(1, queryParams.value.pageNo - 1)
    }
  } finally {
    isLoading.value = false
  }
}

const getFavoriteAudioList = async (append = false) => {
  if(isGuestUser.value) {
    openLoginPrompt()
    return
  }
  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    const data = await getFavoritesPage({
      ...queryParams.value,
      contentType: AUDIO_CONTENT_TYPE
    })
    const list = data?.list || []
    favoriteAudioList.value = append ? [...favoriteAudioList.value, ...list] : list
    dataTotal.value = data?.total || 0
  } catch {
    if (append) {
      queryParams.value.pageNo = Math.max(1, queryParams.value.pageNo - 1)
    }
  } finally {
    isLoading.value = false
  }
}

const getHistoryAudioList = async (append = false) => {
  if(isGuestUser.value) {
    openLoginPrompt()
    return
  }
  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    const data = await getHistoryPage({
      ...queryParams.value,
      contentType: AUDIO_CONTENT_TYPE
    })
    const list = data?.list || []
    historyAudioList.value = append ? [...historyAudioList.value, ...list] : list
    dataTotal.value = data?.total || 0
  } catch {
    if (append) {
      queryParams.value.pageNo = Math.max(1, queryParams.value.pageNo - 1)
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  loadAudioSeries()
  loadAudioList()
  audioScrollContainer = audioListRef.value
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  audioScrollContainer?.addEventListener('scroll', handlePageScroll, { passive: true })
  handlePageScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handlePageScroll)
  audioScrollContainer?.removeEventListener('scroll', handlePageScroll)
  audioScrollContainer = null
})
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.audio-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #fffaf5 0%, #f8f3ec 52%, #f3eee7 100%);
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.bg-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(90px);
  opacity: 0.55;
}

.orb-1 {
  width: 320px;
  height: 320px;
  top: -100px;
  left: -80px;
  background: rgba(255, 170, 102, 0.42);
}

.orb-2 {
  width: 360px;
  height: 360px;
  right: -100px;
  top: 120px;
  background: rgba(112, 200, 180, 0.26);
}

.orb-3 {
  width: 280px;
  height: 280px;
  bottom: -80px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(120, 164, 255, 0.22);
}

.content-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 100vh;
  height: 100vh;
  overflow: hidden;
}

.sidebar {
  width: 264px;
  flex-shrink: 0;
  height: 100vh;
  overflow: hidden;
  background: #FFF9F5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
  position: relative;
  z-index: 2;
}

.nav-card {
  height: 100%;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}

.nav-logo .logo-icon {
  width: 62px;
  height: 62px;
  background: #fff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.nav-logo .brand-name {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #2D3436;
  letter-spacing: 1px;
  line-height: 1.05;
}

.brand-subtitle {
  color: #a78b76;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.2px;
  line-height: 1;
}

.nav-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: clamp(12px, 2vh, 20px);
  padding: clamp(12px, 2vh, 16px) 12px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 14px;
  color: #666;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  text-decoration: none;
}

.nav-item:hover {
  background: rgba(255, 138, 61, 0.08);
  color: #FF8A3D;
}

.nav-item.active {
  background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%);
  color: #FF6B35;
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.2);
}

.nav-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.nav-menu-visual {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  margin-top: auto;
  border-radius: 0;
  overflow: hidden;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.nav-menu-image {
  display: block;
  flex-shrink: 0;
  width: 100%;
  min-width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center bottom;
}

.audio-shell {
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
  width: min(100%, 860px);
  margin: 0 auto;
  height: 100vh;
  padding: 24px 20px 132px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.audio-header {
  display: none;
}

.mobile-topbar {
  display: none;
}

.mobile-topbar-offset {
  display: none;
}

.back-btn {
  display: none;
  position: absolute;
  top: 8px;
  right: 0;
  width: 46px;
  height: 46px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.75);
  color: #5f5044;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(45, 52, 54, 0.08);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.mobile-back-btn {
  display: none;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  align-items: center;
  justify-content: center;
}

.mobile-topbar-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0 12px;
  color: #2d3436;
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-topbar-spacer {
  display: none;
}

.back-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(45, 52, 54, 0.11);
}

.audio-tabs {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 0;
  flex-wrap: wrap;
}

.audio-tab {
  min-width: 104px;
  min-height: 46px;
  padding: 0 22px;
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.62);
  color: #7b6a5f;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(70, 59, 47, 0.05);
  transition: transform 0.22s ease, background 0.22s ease, color 0.22s ease, box-shadow 0.22s ease;
}

.audio-tab:hover {
  transform: translateY(-1px);
}

.audio-tab.active {
  background: linear-gradient(135deg, #ff9d56 0%, #ff7a33 100%);
  color: #ffffff;
  box-shadow: 0 14px 30px rgba(255, 125, 48, 0.22);
}

.audio-list {
  flex: 1;
  min-height: 0;
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 6px;
  scrollbar-gutter: stable;
}

.audio-item {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 18px 20px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.76);
  border: 1.5px solid rgba(255, 255, 255, 0.72);
  box-shadow:
    0 16px 34px rgba(95, 74, 52, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.audio-skeleton-item {
  cursor: default;
  pointer-events: none;
}

.audio-skeleton-item :deep(.el-skeleton) {
  width: 100%;
}

.audio-skeleton-row {
  display: flex;
  align-items: center;
  gap: 18px;
  width: 100%;
}

.audio-skeleton-icon {
  width: 52px;
  height: 52px;
  flex: 0 0 auto;
}

.audio-skeleton-copy {
  flex: 1;
  min-width: 0;
}

.audio-skeleton-title {
  width: 64%;
  height: 18px;
  border-radius: 999px;
}

.audio-skeleton-date {
  width: 34%;
  height: 14px;
  margin-top: 10px;
  border-radius: 999px;
}

.audio-skeleton-button {
  width: 92px;
  height: 40px;
  border-radius: 999px;
  flex: 0 0 auto;
}

.audio-page-loading {
  width: min(240px, 70%);
  align-self: center;
  padding: 4px 0 2px;
}

.audio-page-loading-line {
  height: 14px;
  border-radius: 999px;
}

.audio-item:hover {
  transform: translateY(-2px);
  box-shadow:
    0 20px 38px rgba(95, 74, 52, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
}

.audio-item.playing {
  transform: scale(1.02);
  border-color: #ff8c42;
  box-shadow: 0 18px 42px rgba(255, 140, 66, 0.22);
  animation: audio-playing-pulse 1s ease-in-out infinite;
}

.audio-item.playing .audio-title {
  color: #ff8c42;
}

.audio-item.playing .audio-item-icon {
  transform: scale(1.2);
}

.audio-item-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  flex-shrink: 0;
  background: #fdf0e3;
  color: #ff8a3d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.86);
  transition: transform 0.22s ease;
}

.audio-item-copy {
  min-width: 0;
  flex: 1;
}

.audio-title {
  margin: 0;
  color: #2d3436;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.35;
  transition: color 0.22s ease;
}

.favorite-btn {
  flex-shrink: 0;
  min-width: 92px;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid rgba(255, 173, 111, 0.4);
  border-radius: 999px;
  background: rgba(255, 247, 239, 0.96);
  color: #d9782f;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.favorite-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(217, 120, 47, 0.14);
}

.favorite-btn:disabled {
  cursor: wait;
  opacity: 0.72;
}

.favorite-btn.active {
  border-color: rgba(255, 143, 66, 0.42);
  background: linear-gradient(135deg, #ffac59 0%, #ff8438 100%);
  color: #ffffff;
  box-shadow: 0 12px 24px rgba(255, 132, 56, 0.2);
}

.audio-player-dock {
  position: fixed;
  left: calc(280px + (100vw - 280px) / 2);
  bottom: 18px;
  transform: translateX(-50%);
  z-index: 30;
  width: min(calc(100vw - 304px), 860px);
  padding: 14px 16px 16px;
  border-radius: 24px;
  background: rgba(255, 250, 245, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.82);
  box-shadow:
    0 18px 40px rgba(95, 74, 52, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.audio-player-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.audio-player-now {
  min-width: 0;
}

.audio-player-label {
  display: block;
  color: #d27a33;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
}

.audio-player-title {
  display: block;
  margin-top: 4px;
  color: #2d3436;
  font-size: 15px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.audio-player {
  width: 100%;
  height: 42px;
}

@keyframes audio-playing-pulse {
  0%,
  100% {
    box-shadow: 0 18px 42px rgba(255, 140, 66, 0.22);
  }

  50% {
    box-shadow: 0 16px 36px rgba(255, 140, 66, 0.16);
  }
}

.player-dock-enter-active,
.player-dock-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.player-dock-enter-from,
.player-dock-leave-to {
  opacity: 0;
  transform: translate(-50%, 10px);
}

.empty-state {
  padding: 48px 20px;
  border-radius: 28px;
  text-align: center;
  color: #9b8878;
  background: rgba(255, 255, 255, 0.58);
  border: 1px dashed rgba(216, 173, 134, 0.5);
}

.empty-state svg {
  font-size: 30px;
  color: #ff8a3d;
}

.empty-state p {
  margin: 14px 0 0;
  font-size: 14px;
  font-weight: 700;
}

@media (max-width: 1200px) {
  .sidebar {
    width: 224px;
  }
}

@media (max-width: 992px) {
  .sidebar {
    width: 208px;
  }

  .nav-logo {
    padding: 20px 20px;
    gap: 16px;
  }

  .nav-logo .logo-icon {
    width: 62px;
    height: 62px;
    font-size: 20px;
  }

  .nav-logo .logo2-icon {
    width: 52px;
    height: 52px;
  }

  .nav-logo .brand-name {
    font-size: 24px;
  }

  .nav-logo .brand-subtitle {
    font-size: 12px;
  }

  .nav-item {
    padding: 16px;
  }

  .audio-player-dock {
    left: calc(208px + (100vw - 208px) / 2);
    width: min(calc(100vw - 260px), 860px);
  }
}

@media (max-width: 768px), (min-width: 769px) and (max-width: 1024px) and (orientation: portrait) {
  .audio-page {
    overflow-y: auto;
  }

  .content-wrapper {
    display: block;
    min-height: 100vh;
    height: auto;
    overflow: visible;
  }

  .sidebar {
    display: none;
  }

  .audio-shell {
    width: 100%;
    height: auto;
    display: block;
    overflow: visible;
    padding: 18px 14px 158px;
  }

  .audio-shell.has-player {
    padding-bottom: 158px;
  }

  .audio-list {
    overflow: visible;
    padding-right: 0;
  }

  .mobile-topbar {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    min-height: 56px;
    padding: calc(env(safe-area-inset-top, 0px) + 6px) 16px 6px;
    z-index: 20;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .mobile-topbar.is-scrolled {
    background: rgba(249, 247, 242, 0.96);
    box-shadow: 0 8px 20px rgba(45, 52, 54, 0.08);
  }

  .mobile-topbar-offset {
    display: block;
    height: calc(env(safe-area-inset-top, 0px) + 56px);
  }

  .back-btn {
    display: none;
  }

  .mobile-back-btn {
    display: inline-flex;
    position: absolute;
    left: 16px;
    top: calc(env(safe-area-inset-top, 0px) + 6px);
  }

  .audio-header {
    min-height: 0;
  }

  .audio-tabs {
    gap: 10px;
  }

  .audio-tab {
    min-width: 90px;
    min-height: 42px;
    padding: 0 18px;
    font-size: 14px;
  }

  .audio-item {
    gap: 14px;
    padding: 16px;
    border-radius: 20px;
  }

  .audio-item-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    font-size: 21px;
  }

  .audio-title {
    font-size: 16px;
  }

  .favorite-btn {
    min-width: 78px;
    min-height: 36px;
    padding: 0 12px;
    gap: 6px;
    font-size: 12px;
  }

  .audio-player-dock {
    left: 50%;
    width: calc(100vw - 16px);
    bottom: 10px;
    padding: 12px 12px 14px;
    border-radius: 20px;
  }

  .audio-player-title {
    font-size: 14px;
  }
}
@media (min-width: 769px) {
  .nav-logo { padding: 20px 20px; gap: 16px; }
  .nav-logo .logo-icon { width: 62px; height: 62px; }
  .nav-logo .logo2-icon { width: 52px; height: 52px; }
  .nav-logo .brand-name { font-size: 24px; letter-spacing: 1px; line-height: 1.05; }
  .nav-logo .brand-subtitle { font-size: 12px; letter-spacing: 1.2px; line-height: 1; }
  .nav-item { padding: clamp(12px, 1.8vh, 18px) 18px; }
}

@media (min-width: 993px) and (max-width: 1200px) {
  .sidebar { width: 224px; }
}

@media (min-width: 769px) and (max-width: 992px) {
  .sidebar { width: 208px; }
}

/* Series detail layout */
.audio-page {
  background: linear-gradient(115deg, #fff7ee 0%, #fffaf5 48%, #f7efe6 100%);
}

.bg-decoration {
  display: none;
}

.audio-shell {
  width: min(100%, 900px);
  padding: 22px 28px 28px;
}

.audio-shell.has-player {
  padding-bottom: 112px;
}

.audio-header {
  display: none;
  min-height: 0;
}

.back-btn {
  display: none;
}

.audio-tabs {
  display: none;
}

.series-hero {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 28px;
  align-items: center;
  margin: 0 0 28px;
}

.series-cover-wrap {
  width: 220px;
  aspect-ratio: 16 / 9;
  border-radius: 17px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 22px 52px rgba(159, 116, 78, 0.16);
}

.series-cover-skeleton,
.series-cover-skeleton :deep(.el-skeleton) {
  width: 100%;
  height: 100%;
}

.series-cover-skeleton-item {
  width: 100%;
  height: 100%;
  border-radius: 0;
}

.series-cover {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.series-copy {
  min-width: 0;
}

.series-title {
  margin: 0;
  color: #111;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.18;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.series-subtitle {
  margin: 10px 0 0;
  color: #161616;
  font-size: 17px;
  font-weight: 500;
  line-height: 1.28;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.series-age-row {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 10px;
}

.series-age-badge {
  min-width: 62px;
  height: 30px;
  padding: 0 11px;
  border-radius: 10px;
  background: linear-gradient(135deg, #ffac59 0%, #ff8438 100%);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
}

.series-description {
  margin: 14px 0 0;
  color: #4f565c;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.55;
  white-space: pre-line;
}

.track-section-title {
  margin: 0 0 14px;
  color: #111;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
}

.audio-list.track-list-panel {
  flex: 1;
  min-height: 0;
  margin: 0;
  gap: 8px;
  padding: 18px 16px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.86);
  box-shadow: 0 18px 48px rgba(112, 83, 55, 0.08);
}

.track-list-panel .audio-item {
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) 84px;
  align-items: center;
  gap: 18px;
  min-height: 82px;
  padding: 0 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.58);
  border: 0;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  cursor: pointer;
  transform: none;
}

.track-list-panel .audio-item:hover,
.track-list-panel .audio-item.playing {
  transform: none;
  box-shadow: none;
  border-color: transparent;
  background: rgba(255, 247, 239, 0.86);
}

.track-play-btn {
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffac59 0%, #ff8438 100%);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(255, 132, 56, 0.18);
}

.track-play-btn svg {
  margin-left: 3px;
}

.audio-item.playing .track-play-btn svg {
  margin-left: 0;
}

.track-list-panel .audio-item-copy {
  min-width: 0;
}

.track-list-panel .audio-title {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 0;
  color: #111;
  font-size: 19px;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.track-index {
  flex: 0 0 auto;
  color: #111;
  font-weight: 700;
}

.audio-item.playing .track-index,
.audio-item.playing .audio-title {
  color: #ff6508;
}

.track-list-panel .favorite-btn {
  width: auto;
  min-width: 76px;
  height: 32px;
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid rgba(255, 173, 111, 0.4);
  border-radius: 999px;
  background: rgba(255, 247, 239, 0.96);
  color: #d9782f;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 800;
  box-shadow: none;
}

.track-list-panel .favorite-btn:hover {
  transform: translateY(-1px);
  color: #d9782f;
  box-shadow: 0 10px 20px rgba(217, 120, 47, 0.14);
}

.track-list-panel .favorite-btn.active {
  border-color: rgba(255, 143, 66, 0.42);
  background: linear-gradient(135deg, #ffac59 0%, #ff8438 100%);
  color: #fff;
  box-shadow: 0 12px 24px rgba(255, 132, 56, 0.2);
}

.track-list-panel .audio-skeleton-item {
  min-height: 82px;
  padding: 0 12px;
  background: transparent;
  border: 0;
  box-shadow: none;
}

@media (max-width: 768px), (min-width: 769px) and (max-width: 1024px) and (orientation: portrait) {
  .audio-shell {
    padding: 18px 14px 158px;
  }

  .audio-shell.has-player {
    padding-bottom: 158px;
  }

  .audio-header,
  .back-btn {
    display: none;
  }

  .series-hero {
    grid-template-columns: 136px minmax(0, 1fr);
    gap: 16px;
    margin: 10px 0 32px;
  }

  .series-cover-wrap {
    width: 136px;
    border-radius: 16px;
  }

  .series-title {
    font-size: 20px;
  }

  .series-subtitle {
    margin-top: 8px;
    font-size: 14px;
  }

  .series-age-row {
    margin-top: 9px;
  }

  .series-age-badge {
    min-width: 54px;
    height: 28px;
    padding: 0 10px;
    border-radius: 9px;
    font-size: 13px;
  }

  .series-description {
    grid-column: 1 / -1;
    margin-top: 12px;
    font-size: 14px;
    line-height: 1.5;
  }

  .track-section-title {
    margin-bottom: 18px;
    font-size: 20px;
  }

  .audio-list.track-list-panel {
    gap: 8px;
    padding: 12px;
    border-radius: 22px;
  }

  .track-list-panel .audio-item {
    grid-template-columns: 40px minmax(0, 1fr) 76px;
    gap: 12px;
    min-height: 76px;
    padding: 0 6px;
    border-radius: 14px;
  }

  .track-play-btn {
    width: 38px;
    height: 38px;
    font-size: 15px;
  }

  .track-list-panel .audio-title {
    gap: 8px;
    font-size: 16px;
  }

  .track-list-panel .favorite-btn {
    min-width: 70px;
    height: 30px;
    min-height: 30px;
    padding: 0 9px;
    gap: 5px;
    font-size: 11px;
  }
}
</style>
